<template>
  <v-card class="mx-auto mb-6 mic-check-card" max-width="420" outlined data-testid="mic_check">
    <v-card-title class="subtitle-1 py-3">
      <v-icon left>mdi-microphone</v-icon>
      {{ $t("mic_check.title") }}
    </v-card-title>
    <v-card-text>
      <v-alert
        v-if="status === 'denied' || status === 'unavailable'"
        type="error"
        prominent
        data-testid="mic_check_denied"
      >
        <div class="subtitle-1">{{ $t("mic_check.denied_title") }}</div>
        <div class="mt-2">{{ denied_message }}</div>
        <v-btn
          class="mt-4"
          color="white"
          outlined
          @click="enable_microphone"
          data-testid="mic_check_retry"
        >
          {{ $t("mic_check.retry") }}
        </v-btn>
      </v-alert>

      <div v-else-if="status === 'checking' || status === 'requesting'" class="py-4">
        <v-progress-linear indeterminate color="primary" data-testid="mic_check_requesting" />
        <div class="mt-3 grey--text">{{ $t("mic_check.requesting") }}</div>
      </div>

      <div v-else-if="status === 'prompt'" class="text-center py-2">
        <v-btn
          color="primary"
          large
          @click="enable_microphone"
          data-testid="mic_check_enable"
        >
          <v-icon left>mdi-microphone</v-icon>
          {{ $t("mic_check.enable_microphone") }}
        </v-btn>
      </div>

      <div v-else-if="status === 'granted'">
        <v-select
          v-model="selected_device_id"
          :items="device_items"
          :label="$t('mic_check.select_microphone')"
          hide-details
          outlined
          dense
          data-testid="mic_check_device_select"
        />
        <div class="mt-4">
          <canvas
            ref="level_canvas"
            class="mic-check-level"
            data-testid="mic_check_level"
          ></canvas>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
const BAR_COUNT = 31;
const MIN_DB = -50;
const MAX_DB = -8;
const ATTACK = 0.4;
const RELEASE = 0.12;
// Speech band: male fundamentals ~85 Hz through vowel formants (~4 kHz).
const SPEECH_MIN_HZ = 80;
const SPEECH_MAX_HZ = 4000;

function hz_to_bin(hz, fft_size, sample_rate) {
  return Math.round((hz * fft_size) / sample_rate);
}

/**
 * RMS amplitude from float time-domain samples in [-1, 1].
 */
function rms_from_time_domain(samples) {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i];
    sum += sample * sample;
  }
  return Math.sqrt(sum / samples.length);
}

/**
 * Map RMS to 0–1 using a speech-friendly dBFS range.
 *
 * Web Audio cannot report dB SPL. Convert RMS to dBFS
 * (20 * log10(rms)) and linearly map a typical mic-check window
 * of -50 dBFS (noise floor) to -8 dBFS (loud speech) so quiet
 * laptop mics still move the visualizer.
 */
function normalize_rms_db(rms) {
  const db = 20 * Math.log10(Math.max(rms, 1e-8));
  return Math.max(0, Math.min(1, (db - MIN_DB) / (MAX_DB - MIN_DB)));
}

function fill_round_rect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  if (r <= 0 || height <= 0 || width <= 0) {
    ctx.fillRect(x, y, width, height);
    return;
  }
  if (typeof ctx.roundRect === "function") {
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, r);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.fill();
}

export default {
  props: {
    instance: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      status: "checking",
      devices: [],
      selected_device_id: "",
      current_stream_device_id: "",
      stream: null,
      audio_context: null,
      analyser: null,
      source_node: null,
      meter_raf: null,
      preview_generation: 0,
    };
  },
  computed: {
    device_items() {
      return this.devices.map((device, index) => ({
        text: device.label || this.$t("mic_check.microphone_n", { n: index + 1 }),
        value: device.deviceId,
      }));
    },
    denied_message() {
      if (this.status === "unavailable") {
        return this.$t("mic_check.unavailable_body");
      }
      return this.$t("mic_check.denied_body");
    },
  },
  watch: {
    instance: {
      immediate: true,
      handler() {
        this.sync_permission();
      },
    },
    selected_device_id(new_id) {
      if (this.status !== "granted" || !new_id) {
        return;
      }
      if (new_id === this.current_stream_device_id) {
        return;
      }
      this.start_preview();
    },
  },
  created() {
    this.on_device_change = () => {
      if (this.status === "granted") {
        this.refresh_devices();
      }
    };
    this.time_data = null;
    this.freq_data = null;
    this.bar_heights = new Array(BAR_COUNT).fill(0);
    this.displayed_level = 0;
    this.canvas_resize_observer = null;
  },
  mounted() {
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", this.on_device_change);
    }
  },
  beforeDestroy() {
    this.stop();
    if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
      navigator.mediaDevices.removeEventListener("devicechange", this.on_device_change);
    }
  },
  methods: {
    get_device_id() {
      return this.selected_device_id || undefined;
    },
    stop() {
      this.stop_preview();
    },
    async sync_permission() {
      if (!this.instance || typeof this.instance.checkMicrophonePermission !== "function") {
        return;
      }
      this.status = "checking";
      try {
        const result = await this.instance.checkMicrophonePermission();
        this.apply_permission_result(result);
        if (result && result.status === "granted") {
          await this.start_preview();
        }
      } catch (error) {
        console.error("Failed to check microphone permission:", error);
        this.status = "unavailable";
        this.$emit("denied");
      }
    },
    async enable_microphone() {
      if (!this.instance || typeof this.instance.requestMicrophonePermission !== "function") {
        return;
      }
      this.status = "requesting";
      try {
        const result = await this.instance.requestMicrophonePermission();
        this.apply_permission_result(result);
        if (result && result.status === "granted") {
          await this.start_preview();
        }
      } catch (error) {
        console.error("Failed to request microphone permission:", error);
        this.status = "denied";
        this.$emit("denied");
      }
    },
    apply_permission_result(result) {
      const status = (result && result.status) || "unavailable";
      this.status = status;
      if (status === "granted") {
        this.$emit("granted");
        return;
      }
      if (status === "denied" || status === "unavailable") {
        this.$emit("denied");
      }
    },
    async start_preview() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.status = "unavailable";
        this.$emit("denied");
        return;
      }

      const generation = ++this.preview_generation;
      this.stop_preview_graph();

      const constraints = this.selected_device_id
        ? { audio: { deviceId: { exact: this.selected_device_id } } }
        : { audio: true };

      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (generation !== this.preview_generation) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        this.stream = stream;
        const track = stream.getAudioTracks()[0];
        const settings_id = track && track.getSettings && track.getSettings().deviceId;
        if (settings_id) {
          this.current_stream_device_id = settings_id;
          this.selected_device_id = settings_id;
        }
        await this.refresh_devices();
        this.setup_analyser(stream);
      } catch (error) {
        if (generation !== this.preview_generation) {
          return;
        }
        console.error("Failed to open microphone preview:", error);
        this.status = "denied";
        this.$emit("denied");
      }
    },
    async refresh_devices() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.devices = devices.filter((device) => device.kind === "audioinput" && device.deviceId);
      if (
        this.selected_device_id &&
        this.devices.length > 0 &&
        !this.devices.some((device) => device.deviceId === this.selected_device_id)
      ) {
        this.selected_device_id = this.devices[0].deviceId;
      }
    },
    setup_analyser(stream) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        return;
      }
      const audio_context = new AudioContext();
      if (audio_context.state === "suspended") {
        audio_context.resume();
      }
      const source_node = audio_context.createMediaStreamSource(stream);
      const analyser = audio_context.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.75;
      analyser.minDecibels = -70;
      analyser.maxDecibels = -20;
      source_node.connect(analyser);
      this.audio_context = audio_context;
      this.source_node = source_node;
      this.analyser = analyser;
      this.time_data = new Float32Array(analyser.fftSize);
      this.freq_data = new Uint8Array(analyser.frequencyBinCount);
      this.bar_heights = new Array(BAR_COUNT).fill(0);
      this.displayed_level = 0;
      this.$nextTick(() => {
        this.size_canvas();
        this.observe_canvas_size();
        this.start_meter();
      });
    },
    size_canvas() {
      const canvas = this.$refs.level_canvas;
      if (!canvas) {
        return;
      }
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width * dpr));
      const height = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
    },
    observe_canvas_size() {
      const canvas = this.$refs.level_canvas;
      if (!canvas || typeof ResizeObserver === "undefined") {
        return;
      }
      if (this.canvas_resize_observer) {
        this.canvas_resize_observer.disconnect();
      }
      this.canvas_resize_observer = new ResizeObserver(() => {
        this.size_canvas();
      });
      this.canvas_resize_observer.observe(canvas);
    },
    start_meter() {
      const analyser = this.analyser;
      if (!analyser) {
        return;
      }
      const tick = () => {
        this.meter_raf = requestAnimationFrame(tick);
        this.draw_level(analyser);
      };
      this.meter_raf = requestAnimationFrame(tick);
    },
    draw_level(analyser) {
      const canvas = this.$refs.level_canvas;
      if (!canvas || !this.time_data || !this.freq_data) {
        return;
      }
      analyser.getFloatTimeDomainData(this.time_data);
      analyser.getByteFrequencyData(this.freq_data);

      const rms = rms_from_time_domain(this.time_data);
      const normalized = normalize_rms_db(rms);
      const smoothing = normalized > this.displayed_level ? ATTACK : RELEASE;
      this.displayed_level += (normalized - this.displayed_level) * smoothing;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const gap = 3;
      const bar_width = Math.max(2, (width - gap * (BAR_COUNT - 1)) / BAR_COUNT);
      const max_bar_height = height * 0.92;
      const center_y = height / 2;
      const sample_rate = (this.audio_context && this.audio_context.sampleRate) || 48000;
      const fft_size = analyser.fftSize;
      const min_bin = Math.max(1, hz_to_bin(SPEECH_MIN_HZ, fft_size, sample_rate));
      const max_bin = Math.min(
        this.freq_data.length - 1,
        Math.max(min_bin + BAR_COUNT, hz_to_bin(SPEECH_MAX_HZ, fft_size, sample_rate)),
      );
      const min_log = Math.log(min_bin);
      const max_log = Math.log(max_bin);
      const level = this.displayed_level;
      const active = 0.15 + 0.85 * level;

      for (let i = 0; i < BAR_COUNT; i++) {
        const t0 = i / BAR_COUNT;
        const t1 = (i + 1) / BAR_COUNT;
        const start = Math.floor(Math.exp(min_log + t0 * (max_log - min_log)));
        const end = Math.max(start + 1, Math.floor(Math.exp(min_log + t1 * (max_log - min_log))));
        let sum = 0;
        for (let bin = start; bin < end && bin < this.freq_data.length; bin++) {
          sum += this.freq_data[bin];
        }
        const energy = Math.pow(sum / ((end - start) * 255), 0.7);
        const target = Math.max(0.06, energy * active);
        this.bar_heights[i] += (target - this.bar_heights[i]) * 0.35;

        const bar_height = Math.max(3, this.bar_heights[i] * max_bar_height);
        const x = i * (bar_width + gap);
        const y = center_y - bar_height / 2;
        const mix = Math.min(1, this.bar_heights[i] * (0.4 + 0.6 * level));
        const grey = 158;
        const green_r = 76;
        const green_g = 175;
        const green_b = 80;
        const r = Math.round(grey + (green_r - grey) * mix);
        const g = Math.round(grey + (green_g - grey) * mix);
        const b = Math.round(grey + (green_b - grey) * mix);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        fill_round_rect(ctx, x, y, bar_width, bar_height, Math.min(3, bar_width / 2));
      }
    },
    stop_preview() {
      this.preview_generation += 1;
      this.stop_preview_graph();
      this.displayed_level = 0;
      this.current_stream_device_id = "";
    },
    stop_preview_graph() {
      if (this.canvas_resize_observer) {
        this.canvas_resize_observer.disconnect();
        this.canvas_resize_observer = null;
      }
      if (this.meter_raf !== null) {
        cancelAnimationFrame(this.meter_raf);
        this.meter_raf = null;
      }
      if (this.source_node) {
        this.source_node.disconnect();
        this.source_node = null;
      }
      this.analyser = null;
      if (this.audio_context) {
        this.audio_context.close();
        this.audio_context = null;
      }
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },
  },
};
</script>

<style scoped>
.mic-check-card {
  text-align: left;
}
.mic-check-level {
  display: block;
  width: 100%;
  height: 56px;
}
</style>
